import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SyncLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { SERVE_FIRST_LOGO as serveFirstLogo } from "../../../constants/branding";
import { postData } from "../../helpers/fetch_services";
import {
  isServeFirstBrandingHidden,
  setServeFirstBrandingHidden,
} from "../../../helpers/serveFirstBranding";
import {
  bootstrapConversationalSurvey,
  submitConversationalAnswers,
} from "../../helpers/conversationalSurvey";
import {
  CSAT_ERROR_CODES,
  csatErrorCode,
  rememberCsatToken,
} from "../../../helpers/csatToken";
import {
  getSurveyStreamUrl,
  streamSurveyChat,
} from "../../helpers/streamSurveyChat";
import {
  AiSparkIcon,
  AvatarSparkleIcon,
  CheckIcon,
  SendIcon,
} from "./EvaIcons";
import "./evaChat.css";

const INPUT_MAX_HEIGHT = 110;

function EvaAvatar() {
  return (
    <div className="eva-chat__avatar">
      <AvatarSparkleIcon />
    </div>
  );
}

function EvaBubble({ text, showLabel, isError }) {
  return (
    <div className="eva-chat__row eva-chat__row--eva">
      <EvaAvatar />
      <div
        className={`eva-chat__bubble eva-chat__bubble--eva${
          isError ? " eva-chat__bubble--error" : ""
        }`}
      >
        {showLabel && (
          <div className="eva-chat__bubble-label">
            <AiSparkIcon size={12} />
            <span>Generated by EVA</span>
          </div>
        )}
        <p className="eva-chat__bubble-text">{text || "…"}</p>
      </div>
    </div>
  );
}

function UserBubble({ text }) {
  return (
    <div className="eva-chat__row eva-chat__row--user">
      <div className="eva-chat__bubble eva-chat__bubble--user">
        <p className="eva-chat__bubble-text">{text}</p>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="eva-chat__row eva-chat__row--eva">
      <EvaAvatar />
      <div className="eva-chat__bubble eva-chat__bubble--eva eva-chat__bubble--typing">
        <div className="eva-chat__typing" aria-label="EVA is writing">
          <span />
          <span />
          <span />
          <p className="eva-chat__typing-text">EVA is writing…</p>
        </div>
      </div>
    </div>
  );
}

function SurveyCompleteCard({ subtitle }) {
  return (
    <div className="eva-chat__complete-card">
      <div className="eva-chat__complete-badge">
        <CheckIcon />
      </div>
      <p className="eva-chat__complete-title">Survey Complete!</p>
      <p className="eva-chat__complete-subtitle">
        {subtitle ||
          "Thank you for your feedback. Your responses help us improve."}
      </p>
    </div>
  );
}

/**
 * SF-3605: Chat-style respondent UI for conversational (Eva) surveys.
 * Eva design from eva-design-change; streaming/submit from SF-3605.
 */
function ConversationalSurveyPage() {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const [hideServeFirst, setHideServeFirst] = useState(
    isServeFirstBrandingHidden,
  );

  const bootstrapRef = useRef(location.state?.conversationalBootstrap || null);
  const surveyMeta =
    location.state?.surveyWithoutLogin || location.state?.survey || null;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [chatToken, setChatToken] = useState(null);
  const [chatServiceUrl, setChatServiceUrl] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ answered: 0, total: 0 });
  const [pendingDone, setPendingDone] = useState(null);
  const [showContact, setShowContact] = useState(false);
  const [contactDetails, setContactDetails] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [closingNote, setClosingNote] = useState("");
  const [googleReview, setGoogleReview] = useState(false);
  const [googleReviewUrl, setGoogleReviewUrl] = useState(
    location.state?.locationGoogleReviewUrl ||
      location.state?.surveyWithoutLogin?.locationGoogleReviewUrl ||
      "",
  );
  const lastActivityRef = useRef(Date.now());
  const latestAnswersRef = useRef({});

  const bottomRef = useRef(null);
  const abortRef = useRef(null);
  const startedRef = useRef(false);
  const textareaRef = useRef(null);

  const resetInputSize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.overflowY = "hidden";
  };

  const scrollToBottom = useCallback(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streaming, completed, showContact, scrollToBottom]);

  const appendMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const patchLastAssistant = useCallback((patch) => {
    setMessages((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].role === "assistant") {
          next[i] = { ...next[i], ...patch };
          break;
        }
      }
      return next;
    });
  }, []);

  const appendToLastAssistant = useCallback((delta) => {
    setMessages((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].role === "assistant") {
          next[i] = {
            ...next[i],
            content: `${next[i].content || ""}${delta}`,
            streaming: true,
          };
          break;
        }
      }
      return next;
    });
  }, []);

  const runStream = useCallback(
    async ({ token, serviceUrl, sid, message }) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setStreaming(true);
      appendMessage({ role: "assistant", content: "", streaming: true });

      let donePayload = null;
      try {
        const url = getSurveyStreamUrl(serviceUrl);
        // eslint-disable-next-line no-restricted-syntax
        for await (const event of streamSurveyChat({
          url,
          authToken: token,
          body: { sessionId: sid, message },
          signal: controller.signal,
        })) {
          if (event.type === "text_delta" && event.content) {
            appendToLastAssistant(event.content);
          } else if (event.type === "tool_start") {
            patchLastAssistant({ toolStatus: event.tool || "working…" });
          } else if (event.type === "tool_result") {
            patchLastAssistant({ toolStatus: null });
          } else if (event.type === "done") {
            donePayload = event;
            // Prefer done.message: server merges/dedupes across tool rounds + empty
            // retries. Streamed text can be longer and more repetitive.
            const doneText =
              typeof event.message === "string" ? event.message.trim() : "";
            setMessages((prev) => {
              const next = [...prev];
              for (let i = next.length - 1; i >= 0; i -= 1) {
                if (next[i].role !== "assistant") continue;
                const streamed = (next[i].content || "").trim();
                next[i] = {
                  ...next[i],
                  content: doneText || streamed,
                  streaming: false,
                  toolStatus: null,
                };
                break;
              }
              return next;
            });
            if (event.answers) {
              latestAnswersRef.current = event.answers;
              const total =
                config?.mandatory_question_ids?.length ||
                event.conversation?.capture_stats?.total_questions ||
                Object.keys(event.answers).length;
              const captured = Object.values(event.answers).filter(
                (a) => a && a.capture_method !== "skipped",
              ).length;
              setProgress({
                answered: event.surveyComplete ? total : captured,
                total,
              });
            }
            if (event.surveyComplete) {
              setCompleted(true);
              if (event.acknowledgmentText) {
                setClosingNote(event.acknowledgmentText);
              }
              if (event.googleReviewSuggested) {
                setGoogleReview(true);
              }
            }
            lastActivityRef.current = Date.now();
          } else if (event.type === "error") {
            patchLastAssistant({
              content: event.message || "Something went wrong.",
              streaming: false,
              isError: true,
            });
          }
        }
      } catch (err) {
        if (err?.name === "AbortError") return null;
        patchLastAssistant({
          content: err?.message || "Connection error. Please try again.",
          streaming: false,
          isError: true,
        });
        throw err;
      } finally {
        setStreaming(false);
      }
      return donePayload;
    },
    [appendMessage, appendToLastAssistant, patchLastAssistant, config],
  );

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        let boot = bootstrapRef.current;
        const query = new URLSearchParams(location.search || "");
        const surveyId =
          query.get("survey_id") ||
          localStorage.getItem("survey") ||
          surveyMeta?._id ||
          surveyMeta?.id;
        // Opened straight from a link (Login and Welcome navigate here without
        // a query): the link decides the CSAT token, as Login does — a public
        // link without one must not answer an earlier CSAT email of this tab.
        if (query.get("survey_id")) {
          rememberCsatToken(query.get("csat_token"), query.get("survey_id"));
        }

        if (surveyId) {
          try {
            const brandResult = await postData("api/getBrandDetail", {
              survey_id: surveyId,
            });
            if (brandResult?.status === 200) {
              setHideServeFirst(
                setServeFirstBrandingHidden(brandResult?.data?.hideServeFirst),
              );
            }
          } catch {
            // Branding failure must not prevent the survey from starting.
          }
        }

        const bootstrapLocationId =
          query.get("location_id") || localStorage.getItem("location") || null;

        try {
          if (
            surveyId &&
            sessionStorage.getItem(`sf_conv_submitted_${surveyId}`) === "1"
          ) {
            history.replace({
              pathname: "/surveyThankYou",
              state: { surveyWithoutLogin: true },
            });
            return;
          }
        } catch {
          /* ignore */
        }

        if (!boot) {
          if (!surveyId) {
            throw new Error("Missing survey id for conversational survey");
          }
          boot = await bootstrapConversationalSurvey({
            survey_id: surveyId,
            location_id: bootstrapLocationId,
          });
        }

        setConfig(boot.config);
        setChatToken(boot.chatToken);
        setChatServiceUrl(boot.chatServiceUrl);
        setSessionId(boot.sessionId);
        if (boot.config?.location_google_review_url) {
          setGoogleReviewUrl(boot.config.location_google_review_url);
        }
        setProgress({
          answered: 0,
          total: boot.config?.mandatory_question_ids?.length || 0,
        });
        try {
          if (boot.config?.survey_id) {
            localStorage.setItem("survey", boot.config.survey_id);
          }
          if (boot.config?.company_id) {
            localStorage.setItem("company_id", boot.config.company_id);
          }
          if (boot.config?.location_id) {
            localStorage.setItem("location", boot.config.location_id);
          }
        } catch {
          /* ignore */
        }
        setLoading(false);

        const done = await runStream({
          token: boot.chatToken,
          serviceUrl: boot.chatServiceUrl,
          sid: boot.sessionId,
          message: "__start__",
        });
        // Same path as sendMessage — bootstrap can complete on the opening turn
        // (e.g. finish_survey); pass boot.config because React state may not have flushed.
        if (done?.surveyComplete) {
          await handleSurveyComplete(done, boot.config);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        setError(err?.message || "Failed to start conversational survey");
        setLoading(false);
        if (err?.code === "NOT_CONVERSATIONAL") {
          history.replace("/survey/questions", {
            surveyWithoutLogin: surveyMeta || true,
          });
        }
      }
    };

    init();

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSurveyComplete = async (donePayload, configOverride = null) => {
    const activeConfig = configOverride || config;
    const needsContact =
      donePayload.contactRequired ||
      activeConfig?.contact_info_required ||
      false;
    if (needsContact) {
      setPendingDone(donePayload);
      setShowContact(true);
      return;
    }
    await finalizeAndSubmit(donePayload, contactDetails, activeConfig);
  };

  const finalizeAndSubmit = async (
    donePayload,
    contacts = {},
    configOverride = null,
  ) => {
    if (submitting) return;
    setSubmitting(true);
    const activeConfig = configOverride || config;
    try {
      const result = await submitConversationalAnswers({
        answers: donePayload.answers || latestAnswersRef.current || {},
        conversation: donePayload.conversation || {},
        contactDetails: contacts,
        config: activeConfig,
      });
      try {
        localStorage.removeItem(`sf_conv_partial_${sessionId}`);
        const sid = activeConfig?.survey_id || localStorage.getItem("survey");
        if (sid) {
          sessionStorage.setItem(`sf_conv_submitted_${sid}`, "1");
        }
      } catch {
        /* ignore */
      }
      const thanks =
        donePayload.acknowledgmentText ||
        activeConfig?.acknowledgment_text ||
        intl.formatMessage({
          id: "thank-you",
          defaultMessage: "Thank you for your feedback!",
        });
      toast.success(thanks);
      setClosingNote(thanks);
      const suggestGoogle =
        Boolean(donePayload.googleReviewSuggested) ||
        Boolean(activeConfig?.show_google_ratings);
      if (suggestGoogle) {
        setGoogleReview(true);
      }
      const goThanks = () => {
        history.replace({
          pathname: "/surveyThankYou",
          state: {
            surveyWithoutLogin: true,
            uniqueshortcode: result?.data?.results?.uniqueshortcode || "",
          },
        });
      };
      if (suggestGoogle) {
        setTimeout(goThanks, 4000);
      } else {
        goThanks();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      const csatCode = csatErrorCode(err);
      if (csatCode === CSAT_ERROR_CODES.ALREADY_SUBMITTED) {
        // This CSAT email was already answered (another tab, a double tap).
        history.replace("/sameDaySurvey");
        return;
      }
      toast.error(
        csatCode === CSAT_ERROR_CODES.TOKEN_INVALID
          ? intl.formatMessage({ id: "csat-link-invalid" })
          : err?.message || "Failed to submit survey answers",
      );
      setSubmitting(false);
    }
  };

  const sendMessage = async (raw) => {
    const text = (raw ?? input).trim();
    if (
      !text ||
      streaming ||
      completed ||
      !sessionId ||
      !chatToken ||
      showContact
    ) {
      return;
    }

    setInput("");
    resetInputSize();
    appendMessage({ role: "user", content: text });
    lastActivityRef.current = Date.now();

    try {
      const done = await runStream({
        token: chatToken,
        serviceUrl: chatServiceUrl,
        sid: sessionId,
        message: text,
      });

      if (done?.surveyComplete) {
        await handleSurveyComplete(done);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to send message");
    }
  };

  const submitContactAndFinish = async (e) => {
    e?.preventDefault?.();
    const fields = config?.contact_fields || {};
    if (fields.name?.required && !contactDetails.name.trim()) {
      toast.error(fields.name?.label || "Name is required");
      return;
    }
    if (fields.email?.required && !contactDetails.email.trim()) {
      toast.error(fields.email?.label || "Email is required");
      return;
    }
    if (fields.phone?.required && !contactDetails.phone.trim()) {
      toast.error(fields.phone?.label || "Phone is required");
      return;
    }
    setShowContact(false);
    if (pendingDone) {
      await finalizeAndSubmit(pendingDone, contactDetails);
    }
  };

  useEffect(() => {
    if (!config?.allow_partial_response || !sessionId) return undefined;
    const timer = setInterval(() => {
      try {
        localStorage.setItem(
          `sf_conv_partial_${sessionId}`,
          JSON.stringify({
            answers: latestAnswersRef.current,
            at: new Date().toISOString(),
          }),
        );
      } catch {
        /* ignore quota */
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [config, sessionId]);

  useEffect(() => {
    const minutes = Number(config?.ideal_duration_minutes) || 60;
    const idleMs = Math.max(2, minutes) * 60 * 1000;
    const timer = setInterval(() => {
      if (submitting || showContact) return;
      if (Date.now() - lastActivityRef.current < idleMs) return;
      if (completed && pendingDone) {
        finalizeAndSubmit(pendingDone, contactDetails);
      } else if (!completed) {
        toast.info(
          intl.formatMessage({
            id: "survey-idle",
            defaultMessage:
              "Still there? Send an answer to continue the survey.",
          }),
        );
        lastActivityRef.current = Date.now();
      }
    }, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, completed, pendingDone, submitting, showContact]);

  const handleInputChange = (event) => {
    setInput(event.target.value);
    lastActivityRef.current = Date.now();
    const el = event.target;
    el.style.height = "auto";
    const borders = el.offsetHeight - el.clientHeight;
    const contentHeight = el.scrollHeight + borders;
    el.style.height = `${Math.min(contentHeight, INPUT_MAX_HEIGHT)}px`;
    el.style.overflowY = contentHeight > INPUT_MAX_HEIGHT ? "auto" : "hidden";
  };

  const handleInputKeyDown = (event) => {
    const isEnter = event.key === "Enter" || event.keyCode === 13;
    if (isEnter && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const inputDisabled = streaming || completed || submitting || showContact;
  const canSend = Boolean(input.trim()) && !inputDisabled;

  // First assistant message in the thread gets the "Generated by EVA" label.
  let firstAssistantShown = false;

  if (loading) {
    return (
      <div className="eva-chat eva-chat--centered">
        <SyncLoader color="#501b6c" size={10} />
        <p className="eva-chat__muted">
          {intl.formatMessage({
            id: "starting-survey",
            defaultMessage: "Starting your survey…",
          })}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="eva-chat eva-chat--centered">
        <p className="eva-chat__error">{error}</p>
        <button
          type="button"
          className="eva-chat__restart"
          onClick={() => history.push("/survey/login")}
        >
          {intl.formatMessage({ id: "go-back", defaultMessage: "Go back" })}
        </button>
      </div>
    );
  }

  return (
    <div className="eva-chat">
      <header className="eva-chat__header">
        {!hideServeFirst ? (
          <img
            className="eva-chat__logo"
            src={serveFirstLogo}
            alt="ServeFirst"
          />
        ) : null}
        <div className="eva-chat__badge">
          <AiSparkIcon size={11} />
          <span>AI Survey</span>
        </div>
      </header>

      <main className="eva-chat__main">
        <div className="eva-chat__thread" role="log" aria-live="polite">
          {messages.map((m, idx) => {
            if (m.role === "user") {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <UserBubble key={`user-${idx}`} text={m.content} />
              );
            }

            const isEmptyStreaming =
              m.streaming && !(m.content || "").trim() && !m.isError;
            if (isEmptyStreaming) {
              // eslint-disable-next-line react/no-array-index-key
              return <TypingBubble key={`typing-${idx}`} />;
            }

            const showLabel = !firstAssistantShown && !m.isError;
            if (showLabel) firstAssistantShown = true;

            return (
              <EvaBubble
                // eslint-disable-next-line react/no-array-index-key
                key={`eva-${idx}`}
                text={m.content}
                showLabel={showLabel}
                isError={m.isError}
              />
            );
          })}

          {streaming &&
            messages.length > 0 &&
            messages[messages.length - 1]?.role === "assistant" &&
            messages[messages.length - 1]?.toolStatus &&
            (messages[messages.length - 1]?.content || "").trim() && (
              <TypingBubble />
            )}

          {completed && !showContact && (
            <SurveyCompleteCard subtitle={closingNote} />
          )}

          {googleReview && completed && googleReviewUrl && (
            <div className="eva-chat__google">
              <a
                href={googleReviewUrl}
                target="_blank"
                rel="noreferrer"
                className="eva-chat__quick-reply"
              >
                {intl.formatMessage({
                  id: "leave-google-review",
                  defaultMessage: "Leave a Google review",
                })}
              </a>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </main>

      {showContact && (
        <div className="eva-chat__contact-overlay">
          <form className="eva-chat__contact" onSubmit={submitContactAndFinish}>
            <h2>{config?.contact_info_title || "Contact Information"}</h2>
            {config?.contact_info_description ? (
              <p className="eva-chat__muted">
                {config.contact_info_description}
              </p>
            ) : null}
            {config?.contact_fields?.name?.enabled !== false && (
              <label>
                {config?.contact_fields?.name?.label || "Name"}
                <input
                  value={contactDetails.name}
                  onChange={(e) =>
                    setContactDetails((c) => ({ ...c, name: e.target.value }))
                  }
                  placeholder={config?.contact_fields?.name?.placeholder || ""}
                  required={Boolean(config?.contact_fields?.name?.required)}
                />
              </label>
            )}
            {config?.contact_fields?.email?.enabled !== false && (
              <label>
                {config?.contact_fields?.email?.label || "Email"}
                <input
                  type="email"
                  value={contactDetails.email}
                  onChange={(e) =>
                    setContactDetails((c) => ({ ...c, email: e.target.value }))
                  }
                  placeholder={config?.contact_fields?.email?.placeholder || ""}
                  required={Boolean(config?.contact_fields?.email?.required)}
                />
              </label>
            )}
            {config?.contact_fields?.phone?.enabled && (
              <label>
                {config?.contact_fields?.phone?.label || "Phone"}
                <input
                  value={contactDetails.phone}
                  onChange={(e) =>
                    setContactDetails((c) => ({ ...c, phone: e.target.value }))
                  }
                  placeholder={config?.contact_fields?.phone?.placeholder || ""}
                  required={Boolean(config?.contact_fields?.phone?.required)}
                />
              </label>
            )}
            <button
              type="submit"
              className="eva-chat__contact-submit"
              disabled={submitting}
            >
              {intl.formatMessage({ id: "submit", defaultMessage: "Submit" })}
            </button>
          </form>
        </div>
      )}

      {completed && !showContact ? (
        <div className="eva-chat__ended-bar">
          <span>
            {submitting
              ? intl.formatMessage({
                  id: "submitting",
                  defaultMessage: "Submitting your responses…",
                })
              : "This conversation has ended."}
          </span>
        </div>
      ) : (
        <div className="eva-chat__input-bar">
          <div className="eva-chat__input-inner">
            <textarea
              ref={textareaRef}
              className="eva-chat__input"
              placeholder={intl.formatMessage({
                id: "type-your-answer",
                defaultMessage: "Write here…",
              })}
              rows={1}
              value={input}
              disabled={inputDisabled}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
            />
            <button
              type="button"
              className={`eva-chat__send${canSend ? " eva-chat__send--active" : ""}`}
              onClick={() => sendMessage()}
              disabled={!canSend}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}

      <footer className="eva-chat__footer">
        {!hideServeFirst ? (
          <>
            Powered by{" "}
            <span className="eva-chat__footer-brand">ServeFirst</span> ·{" "}
          </>
        ) : null}
        Your responses are anonymous
        {progress.total > 0 ? (
          <span className="eva-chat__progress-hint">
            {" "}
            · {progress.answered}/{progress.total}
          </span>
        ) : null}
      </footer>
    </div>
  );
}

export default ConversationalSurveyPage;
